import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { initPostHog } from './services/posthogClient'
import './index.css'
import App from './App.jsx'

const CHUNK_LOAD_RELOAD_KEY = '__snowfield_chunk_reload_attempted__'
const MODULE_BOOT_RETRY_KEY = '__snowfield_module_boot_retry__'
const CHUNK_LOAD_ERROR_REGEX = /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk [\w-]+ failed|ChunkLoadError|Cannot read properties of undefined \(reading 'default'\)|Unable to preload CSS/i
const REQUEST_IDLE_CALLBACK_ERROR_REGEX = /requestIdleCallback/i

const getErrorMessage = (error) => {
  if (!error) return ''
  if (typeof error === 'string') return error
  if (error?.message) return error.message
  return String(error)
}

const isChunkLoadError = (error) => CHUNK_LOAD_ERROR_REGEX.test(getErrorMessage(error))
const isRequestIdleCallbackError = (error) => REQUEST_IDLE_CALLBACK_ERROR_REGEX.test(getErrorMessage(error))
const isSentryNoiseError = (error) => isChunkLoadError(error) || isRequestIdleCallbackError(error)

const shouldDropSentryEvent = (event, hint) => {
  if (isSentryNoiseError(hint?.originalException) || isSentryNoiseError(event?.message)) {
    return true
  }

  const exceptionValues = event?.exception?.values ?? []
  return exceptionValues.some((exception) => isSentryNoiseError(exception?.value))
}

// Initialize Sentry with core integrations only.
// Replay integration is loaded lazily after first paint to reduce initial bundle.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.PROD ? 'production' : 'development',
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  ignoreErrors: [
    CHUNK_LOAD_ERROR_REGEX,
    REQUEST_IDLE_CALLBACK_ERROR_REGEX,
    /runtime\.sendMessage/i,
    /Failed to register a ServiceWorker|AbortError/i,
  ],
  beforeSend(event, hint) {
    return shouldDropSentryEvent(event, hint) ? null : event
  },
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
})

if (typeof window !== 'undefined') {
  // Clear stale retry flags after a stable boot so future deploys can recover once again.
  if (sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY) === '1') {
    setTimeout(() => sessionStorage.removeItem(CHUNK_LOAD_RELOAD_KEY), 15000)
  }
  if (sessionStorage.getItem(MODULE_BOOT_RETRY_KEY) === '1') {
    setTimeout(() => sessionStorage.removeItem(MODULE_BOOT_RETRY_KEY), 15000)
  }

  const url = new URL(window.location.href)
  if (url.searchParams.has('_r')) {
    url.searchParams.delete('_r')
    window.history.replaceState({}, '', url.toString())
  }

  window.addEventListener('vite:preloadError', (event) => {
    const error = event?.payload ?? event?.detail ?? event
    if (!isChunkLoadError(error)) return

    const hasRetried = sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY) === '1'
    if (hasRetried) {
      return
    }

    event.preventDefault()
    sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, '1')
    window.location.reload()
  })
}

// Lazy-load Sentry Replay after first paint (saves ~30KB from critical path)
// Use typeof check — requestIdleCallback is undefined in Safari < 16.4
// and optional chaining doesn't prevent ReferenceError on undeclared globals.
const _scheduleIdle = typeof requestIdleCallback === 'function'
  ? requestIdleCallback
  : (cb) => setTimeout(cb, 3000);
if (import.meta.env.VITE_SENTRY_DSN) {
  _scheduleIdle(() => {
    Sentry.addIntegration(Sentry.replayIntegration());
  });
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

// Lazy-load PostHog after first paint OR on first user interaction, whichever
// fires sooner. Events fired in the meantime are queued in posthogClient and
// flushed once init completes.
if (typeof window !== 'undefined') {
  let posthogKicked = false
  const kickPostHog = () => {
    if (posthogKicked) return
    posthogKicked = true
    initPostHog()
  }

  const schedulePostHogIdle = typeof requestIdleCallback === 'function'
    ? (cb) => requestIdleCallback(cb, { timeout: 3000 })
    : (cb) => setTimeout(cb, 1500)
  schedulePostHogIdle(kickPostHog)

  const interactionOpts = { once: true, passive: true, capture: true }
  window.addEventListener('click', kickPostHog, interactionOpts)
  window.addEventListener('keydown', kickPostHog, interactionOpts)
  window.addEventListener('touchstart', kickPostHog, interactionOpts)
  window.addEventListener('pointerdown', kickPostHog, interactionOpts)
}
